<template>
  <q-item
    v-bind="{ clickable, disable, dense, active, activeClass, tag, dark, insetLevel, tabindex, manualFocus, focused }"
    class="detail-item"
    :class="detailItemClasses"
    @click="$emit('click')"
  >
    <div
      class="flex no-wrap justify-start items-start self-start iconname-wrapper"
      :class="{'sticky-position blurred-container no-border': sticky}"
      :style="{'top': (sticky ? stickyTop : 0) + 'px'}"
    >
      <q-item-section
        v-if="!withoutIcon"
        class="icon-section"
        side
        :style="iconSectionStyle"
      >
        <q-icon
          v-if="!hasIconSlot && icon"
          :name="icon"
          :class="{'text-red-8': error}"
          :style="iconStyles"
        />
        <slot
          v-else
          name="icon"
        />
      </q-item-section>
      <q-item-section
        v-if="!withoutName"
        class="name-section relative-position"
        side
        top
        :class="nameSectionClasses"
        :style="nameSectionStyle"
      >
        <span
          v-if="!hasNameSlot"
          :class="{'required-astrx': required}"
        >
          {{ name }}
          <help-button
            v-if="helpFilter"
            :filter="helpFilter"
            dense
            class="relative-position q-ml-xs"
            style="top: -1px;"
          />
        </span>
        <template v-else>
          <slot name="name" />
        </template>
      </q-item-section>
    </div>
    <div
      class="value-section"
      :style="valueSectionStyle"
      :class="valueClasses"
    >
      <nl2br
        v-if="!hasValueSlot"
        tag="div"
        :text="'' + (value || '')"
      />
      <template v-else>
        <slot />
      </template>
    </div>
    <q-item-section
      v-if="viewMore"
      top
      side
      class="more-section self-start"
      :style="moreSectionStyle"
    >
      <q-icon
        name="mdi-chevron-right"
        size="1.4em"
        class="q-mt-xs"
      />
    </q-item-section>
  </q-item>
</template>

<script>
import itemMixin from 'src/helpers/item'
import { strToObj } from 'src/helpers/globals'

/**
 * DetailItem provides a responsive three-column layout for displaying model details
 *
 * SLOTS:
 * - icon: override default icon string with custom elements
 * - name: override default name string with custom elements
 * - default: override default value string with advanced data
 *
 * EVENTS:
 * - click: if clickable=true, emit when this element is clicked
 */
export default {
  mixins: [itemMixin],
  props: {
    // Icon to display
    icon: {
      type: String
    },
    // Name of data
    name: {
      type: String
    },
    // Value of data
    value: {
      type: [String, Number]
    },
    // Spacing between "Name" column and "Value" column (pixels)
    nameSpacing: {
      type: Number,
      default: 150
    },
    // Will default to the mobile layout where Value is on a separate line to Icon+Name
    large: {
      type: Boolean,
      default: false
    },
    // If an input is supplied inside the value slot, the content will be pushed upward to keep the input text in line with the name slot
    // Optionally define a number (in pixels) if the height of the content is not 40px
    withInput: {
      type: [Boolean, Number],
      default: false
    },
    // Remove icon column
    withoutIcon: {
      type: Boolean,
      default: false
    },
    // Remove name column
    withoutName: {
      type: Boolean,
      default: false
    },
    // If supplied, a > arrow will appear at the right, denoting "View More" details
    viewMore: {
      type: Boolean,
      default: false
    },
    // If marked as required, an * will appear in red next to the name
    required: {
      type: Boolean,
      default: false
    },
    // If sticky is enabled, the Icon and Name will remain stuck to the top as scrolled down (only applicable if this DetailItem is quite long
    sticky: {
      type: Boolean,
      default: false
    },
    // Pixels from top to remain sticky
    stickyTop: {
      type: Number,
      default: 0
    },
    // CSS styles to apply to the name
    nameStyles: {
      type: Object,
      default: () => {
        return {}
      }
    },
    // Classes to apply to the name
    nameClasses: {
      type: [String, Object]
    },
    valueClasses: {
      type: [String, Object]
    },
    // CSS styles to apply to the icon
    iconStyles: {
      type: Object,
      default: () => {
        return {}
      }
    },
    // Highlight the item as an error
    error: {
      type: Boolean,
      default: false
    },
    // Optionally display a help button next to the item name which links to the help center with the specified search term
    helpFilter: {
      type: String,
      default: null
    }
  },
  computed: {
    hasNameSlot () {
      return !!this.$slots.name
    },
    hasValueSlot () {
      return !!this.$slots.default
    },
    hasIconSlot () {
      return !!this.$slots.icon
    },
    detailItemClasses () {
      return {
        'large': this.large,
        'with-input': this.withInput,
        // A row with nothing to show in the icon column collapses it on the stacked (phone) layout
        'without-icon': this.withoutIcon || (!this.icon && !this.hasIconSlot),
        'without-name': this.withoutName || (!this.name && !this.hasNameSlot),
        'bg-red-1': this.error
      }
    },
    withInputSize () {
      if (!this.withInput) {
        return 26
      }
      if (this.withInput === true) {
        return 40
      }
      return this.withInput
    },
    iconSectionStyle () {
      return {
        'min-height': this.withInputSize + 'px'
      }
    },
    nameSectionClasses () {
      return strToObj(this.nameClasses, {
        'text-bold text-red-8': this.error
      })
    },
    nameSectionStyle () {
      return {
        'min-height': this.withInputSize + 'px',
        'width': this.nameSpacing + 'px',
        'flex': '0 0 ' + this.nameSpacing + 'px',
        ...this.nameStyles
      }
    },
    valueSectionStyle () {
      return {
        'min-height': this.withInputSize + 'px'
      }
    },
    moreSectionStyle () {
      return {
        'min-height': this.withInputSize + 'px'
      }
    }
  }
}
</script>
